import _ from 'lodash';
import queryString from 'query-string';
import urls from '../../../util/urls';
import fhirActionTypes from './actionTypes';
import { projectPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';

function getResponseContextProps(contextKey) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: data => data,
  };
}

function getParsedResponseContext(contextKey) {
  if (!contextKey) return null;

  return {
    contextKey,
    parseResponseItems: data => _.get(data, 'entry', []),
    parseNextToken: data => {
      const links = _.get(data, 'link', []);
      const nextLink = links.find(link => _.get(link, 'relation') === 'next');
      return nextLink ? _.get(nextLink, 'url') : undefined;
    },
  };
}

function addDatasetAndNextToParams(state, next, params) {
  const project = projectPickerSelectors.selectActiveProject(state);
  let result = params;
  if (project) {
    let _tag = `http://lifeomic.com/fhir/dataset|${project.id}`;
    if (params._tag) {
      // _tag already specified, add to request
      if (Array.isArray(params._tag)) {
        _tag = [...params._tag, _tag];
      } else {
        _tag = [params._tag, _tag];
      }
    }

    result = { ...params, _tag };
  }

  if (next) {
    const parts = next.split('?');
    result = { ...queryString.parse(parts.pop()), ...result };
  }

  return result;
}

function addDatasetToMetaTag(state, tag = []) {
  const project = projectPickerSelectors.selectActiveProject(state);
  if (!project) {
    return tag;
  }
  return [
    {
      code: project.id,
      system: 'http://lifeomic.com/fhir/dataset',
    },
  ];
}

function search({
  next,
  params,
  createUrl,
  actionType,
  errorActionType,
  contextKey,
  isPostQuery,
}) {
  function searchError(error) {
    return {
      type: errorActionType,
      error,
    };
  }

  return async (dispatch, getState) => {
    const allParams = addDatasetAndNextToParams(getState(), next, params);
    const url = createUrl({
      account: getActiveAccount(getState(), searchError),
      params: allParams,
      isPostQuery,
    });

    await dispatch({
      type: actionType,
      url,
      // use `application/json` to allow the response to be compressed by the server:
      fetchOptions: isPostQuery
        ? getPostQueryFetchOptions(allParams)
        : { headers: { Accept: 'application/json' } },
      responseContext: getParsedResponseContext(contextKey),
    });
  };
}

const getPostQueryFetchOptions = params => ({
  body: queryString.stringify(params),
  method: 'POST',
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    Accept: 'application/json',
  },
});

function fetchConditions(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.condition,
    actionType: fhirActionTypes.FETCH_CONDITIONS,
    errorActionType: fhirActionTypes.FETCH_CONDITIONS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchCondition(id, params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_CONDITION,
      url: urls.api.fhir.condition({
        account: getActiveAccount(getState(), fetchConditionError),
        id,
        params,
      }),
    });
  };
}

function fetchConditionError(error) {
  return {
    type: fhirActionTypes.FETCH_CONDITION_ERROR,
    error,
  };
}

function fetchEncounters(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.encounter,
    actionType: fhirActionTypes.FETCH_ENCOUNTERS,
    errorActionType: fhirActionTypes.FETCH_ENCOUNTERS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchImmunizations(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.immunization,
    actionType: fhirActionTypes.FETCH_IMMUNIZATIONS,
    errorActionType: fhirActionTypes.FETCH_IMMUNIZATIONS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchImmunization(id, params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_IMMUNIZATION,
      url: urls.api.fhir.immunization({
        account: getActiveAccount(getState(), fetchImmunizationError),
        id,
        params,
      }),
    });
  };
}

function fetchImmunizationError(error) {
  return {
    type: fhirActionTypes.FETCH_IMMUNIZATION_ERROR,
    error,
  };
}

function fetchObservations(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.observation,
    actionType: fhirActionTypes.FETCH_OBSERVATIONS,
    errorActionType: fhirActionTypes.FETCH_OBSERVATIONS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchObservation(id, params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_OBSERVATION,
      url: urls.api.fhir.observation({
        account: getActiveAccount(getState(), fetchObservationError),
        id,
        params,
      }),
    });
  };
}

function fetchObservationError(error) {
  return {
    type: fhirActionTypes.FETCH_OBSERVATION_ERROR,
    error,
  };
}

function fetchPatients(params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_PATIENTS,
      url: urls.api.fhir.patient({
        account: getActiveAccount(getState(), fetchPatientsError),
        params,
      }),
    });
  };
}

function fetchPatientsWithContext(
  params = {},
  contextKey = null,
  isPostQuery = false,
) {
  return async (dispatch, getState) => {
    const storedParams = _.get(getState(), 'patientsPage.searchParams', {});
    const searchParams = { ...storedParams, ...params };
    const url = urls.api.fhir.patient({
      account: getActiveAccount(getState(), fetchPatientsError),
      isPostQuery,
      params: searchParams,
    });

    await dispatch({
      type: fhirActionTypes.FETCH_PATIENTS,
      url,
      fetchOptions: isPostQuery ? getPostQueryFetchOptions(searchParams) : {},
      responseContext: getParsedResponseContext(contextKey),
    });
  };
}

function fetchPatientsError(error) {
  return {
    type: fhirActionTypes.FETCH_PATIENTS_ERROR,
    error,
  };
}

function fetchPatient(id, params = {}, contextKey) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_PATIENT,
      url: urls.api.fhir.patient({
        account: getActiveAccount(getState(), fetchPatientError),
        id,
        params,
      }),
      responseContext: getResponseContextProps(contextKey),
    });
  };
}

function fetchPatientError(error) {
  return {
    type: fhirActionTypes.FETCH_PATIENT_ERROR,
    error,
  };
}

function updatePatient(
  patient,
  contextKey,
  notification = new NotificationHelper(),
  params,
) {
  return async (dispatch, getState) => {
    const url = urls.api.fhir.patient({
      account: getActiveAccount(getState(), fetchPatientsError),
      id: _.get(patient, 'id'),
      params,
    });

    await dispatch({
      type: fhirActionTypes.FETCH_PATIENTS,
      url,
      fetchOptions: {
        method: 'PUT',
        body: JSON.stringify(patient),
      },
      responseContext: { contextKey },
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchProcedures(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.procedure,
    actionType: fhirActionTypes.FETCH_PROCEDURES,
    errorActionType: fhirActionTypes.FETCH_PROCEDURES_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchProcedure(id, params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_PROCEDURE,
      url: urls.api.fhir.procedure({
        account: getActiveAccount(getState(), fetchProcedureError),
        id,
        params,
      }),
    });
  };
}

function fetchProcedureError(error) {
  return {
    type: fhirActionTypes.FETCH_PROCEDURE_ERROR,
    error,
  };
}

function fetchSequences(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.sequence,
    actionType: fhirActionTypes.FETCH_SEQUENCES,
    errorActionType: fhirActionTypes.FETCH_SEQUENCES_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchSequence(id, params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_SEQUENCE,
      url: urls.api.fhir.sequence({
        account: getActiveAccount(getState(), fetchSequenceError),
        id,
        params,
      }),
    });
  };
}

function updateSequence(
  id,
  sequence,
  contextKey,
  notification = new NotificationHelper(),
) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.UPDATE_SEQUENCE,
      url: urls.api.fhir.sequence({
        id,
        account: getActiveAccount(getState(), updateSequenceError),
      }),
      fetchOptions: {
        method: 'PUT',
        body: JSON.stringify(sequence),
      },
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function updateSequenceError(error) {
  return {
    type: fhirActionTypes.UPDATE_SEQUENCE_ERROR,
    error,
  };
}

function fetchSequenceError(error) {
  return {
    type: fhirActionTypes.FETCH_SEQUENCE_ERROR,
    error,
  };
}

function fetchDocumentReferences(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.documentReference,
    actionType: fhirActionTypes.FETCH_DOCUMENT_REFERENCES,
    errorActionType: fhirActionTypes.FETCH_DOCUMENT_REFERENCES_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchDocumentReference(id, params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_DOCUMENT_REFERENCE,
      url: urls.api.fhir.documentReference({
        account: getActiveAccount(getState(), fetchDocumentReferenceError),
        id,
        params,
      }),
    });
  };
}

function fetchDocumentReferenceError(error) {
  return {
    type: fhirActionTypes.FETCH_DOCUMENT_REFERENCE_ERROR,
    error,
  };
}

function fetchMedia(params = {}, next, contextKey = null, isPostQuery = false) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.media,
    actionType: fhirActionTypes.FETCH_MEDIA,
    errorActionType: fhirActionTypes.FETCH_MEDIA_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchProcedureRequest(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.procedureRequest,
    actionType: fhirActionTypes.FETCH_PROCEDURE_REQUEST,
    errorActionType: fhirActionTypes.FETCH_PROCEDURE_REQUEST_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchCoverage(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.coverage,
    actionType: fhirActionTypes.FETCH_COVERAGE_REQUEST,
    errorActionType: fhirActionTypes.FETCH_COVERAGE_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchEoBs(params = {}, next, contextKey = null, isPostQuery = false) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.explanationOfBenefit,
    actionType: fhirActionTypes.FETCH_EOB_REQUEST,
    errorActionType: fhirActionTypes.FETCH_EOB_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchMedium(id, params = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_MEDIUM,
      url: urls.api.fhir.media({
        account: getActiveAccount(getState(), fetchMediumError),
        id,
        params,
      }),
    });
  };
}

function fetchMediumError(error) {
  return {
    type: fhirActionTypes.FETCH_MEDIUM_ERROR,
    error,
  };
}

function fetchVariants(params = {}, next, contextKey, isPostQuery = false) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.observation,
    actionType: fhirActionTypes.FETCH_PATIENT_VARIANTS,
    errorActionType: fhirActionTypes.FETCH_PATIENT_VARIANTS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchMedicationStatements(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.medicationStatements,
    actionType: fhirActionTypes.FETCH_PATIENT_MEDICATION_STATEMENTS,
    errorActionType: fhirActionTypes.FETCH_PATIENT_MEDICATION_STATEMENTS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchMedicationAdministrations(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.medicationAdministrations,
    actionType: fhirActionTypes.FETCH_PATIENT_MEDICATION_ADMINISTRATIONS,
    errorActionType:
      fhirActionTypes.FETCH_PATIENT_MEDICATION_ADMINISTRATIONS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchMedicationRequests(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.medicationRequests,
    actionType: fhirActionTypes.FETCH_PATIENT_MEDICATION_REQUESTS,
    errorActionType:
      fhirActionTypes.FETCH_PATIENT_MEDICATION_ADMINISTRATIONS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchMedicationDispense(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.medicationDispense,
    actionType: fhirActionTypes.FETCH_PATIENT_MEDICATION_DISPENSE,
    errorActionType: fhirActionTypes.FETCH_PATIENT_MEDICATION_DISPENSE_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchDiagnosticReports(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.diagnosticReports,
    actionType: fhirActionTypes.FETCH_DIAGNOSTIC_REPORTS,
    errorActionType: fhirActionTypes.FETCH_DIAGNOSTIC_REPORTS_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchQuestionnaires(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.questionnaire,
    actionType: fhirActionTypes.FETCH_QUESTIONNAIRES,
    errorActionType: fhirActionTypes.FETCH_QUESTIONNAIRES_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchQuestionnaire(
  id,
  params = {},
  contextKey,
  notification = new NotificationHelper(),
) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_QUESTIONNAIRE,
      url: urls.api.fhir.questionnaire({
        id,
        params,
        account: getActiveAccount(getState(), fetchQuestionnaireError),
      }),
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchQuestionnaireResponse(
  id,
  params = {},
  contextKey,
  notification = new NotificationHelper(),
) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.FETCH_QUESTIONNAIRE_RESPONSE,
      url: urls.api.fhir.questionnaireResponse({
        id,
        params,
        account: getActiveAccount(getState(), fetchQuestionnaireError),
      }),
      responseContext: getResponseContextProps(contextKey),
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchQuestionnaireError(error) {
  return {
    type: fhirActionTypes.FETCH_QUESTIONNAIRE_ERROR,
    error,
  };
}

function createQuestionnaire(questionnaire = {}) {
  return async (dispatch, getState) => {
    const formattedQuetionnaire = {
      ...questionnaire,
      resourceType: 'Questionnaire',
      meta: {
        ...questionnaire.meta,
        tag: addDatasetToMetaTag(getState(), (questionnaire.meta || {}).tag),
      },
    };

    await dispatch({
      type: fhirActionTypes.CREATE_QUESTIONNAIRE,
      url: urls.api.fhir.questionnaire({
        account: getActiveAccount(getState(), createQuestionnaireError),
      }),
      fetchOptions: {
        method: 'POST',
        body: JSON.stringify(formattedQuetionnaire),
      },
    });
  };
}

function createQuestionnaireError(error) {
  return {
    type: fhirActionTypes.CREATE_QUESTIONNAIRE_ERROR,
    error,
  };
}

function updateQuestionnaire(questionnaire = {}) {
  return async (dispatch, getState) => {
    await dispatch({
      type: fhirActionTypes.UPDATE_QUESTIONNAIRE,
      url: urls.api.fhir.questionnaire({
        id: questionnaire.id,
        account: getActiveAccount(getState(), updateQuestionnaireError),
      }),
      fetchOptions: {
        method: 'PUT',
        body: JSON.stringify(questionnaire),
      },
    });
  };
}

function fetchReferralRequest(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.referralRequest,
    actionType: fhirActionTypes.FETCH_REFERRAL_REQUEST,
    errorActionType: fhirActionTypes.FETCH_REFERRAL_REQUEST_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchSpecimen(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.specimen,
    actionType: fhirActionTypes.FETCH_SPECIMEN_REQUEST,
    errorActionType: fhirActionTypes.FETCH_SPECIMEN_REQUEST_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchImagingStudy(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.imagingStudy,
    actionType: fhirActionTypes.FETCH_IMAGING_STUDY_REQUEST,
    errorActionType: fhirActionTypes.FETCH_IMAGING_STUDY_REQUEST_ERROR,
    contextKey,
    isPostQuery,
  });
}

function fetchGoal(params = {}, next, contextKey = null, isPostQuery = false) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.goal,
    actionType: fhirActionTypes.FETCH_GOAL,
    errorActionType: fhirActionTypes.FETCH_GOAL_ERROR,
    contextKey,
    isPostQuery,
  });
}

function updateQuestionnaireError(error) {
  return {
    type: fhirActionTypes.UPDATE_QUESTIONNAIRE_ERROR,
    error,
  };
}

function fetchGraphQLRequest(graphQlRequest, options = {}) {
  return async (dispatch, getState) => {
    const { contextKey, parseResponseItems } = options;

    const notification = options.notification || new NotificationHelper();

    const url = urls.api.fhir.graphQL({
      account: getActiveAccount(getState(), fetchGraphQLRequestError),
    });

    const responseContext = contextKey
      ? {
          contextKey,
          parseResponseItems:
            parseResponseItems || (data => _.get(data, 'data', [])),
        }
      : null;

    await dispatch({
      type: fhirActionTypes.FETCH_FHIR_GRAPHQL,
      url,
      fetchOptions: {
        body: JSON.stringify(graphQlRequest),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      },
      responseContext,
      ...notification.getAsyncRequestHandlers(),
    });
  };
}

function fetchGraphQLRequestError(error) {
  return {
    type: fhirActionTypes.FETCH_FHIR_GRAPHQL_ERROR,
    error,
  };
}

function fetchConsents(
  params = {},
  next,
  contextKey = null,
  isPostQuery = false,
) {
  return search({
    next,
    params,
    createUrl: urls.api.fhir.consent,
    actionType: fhirActionTypes.FETCH_CONSENTS,
    errorActionType: fhirActionTypes.FETCH_CONSENTS_ERROR,
    contextKey,
    isPostQuery,
  });
}

/**
 * Helpers
 */

export function getActiveAccount(store, errorAction) {
  const account = _.get(store, 'account.activeAccount', null);
  if (!account) {
    return errorAction(new Error('Invalid account'));
  }
  return account;
}

export default {
  fetchConditions,
  fetchCondition,
  fetchConditionError,
  fetchConsents,
  fetchEncounters,
  fetchImmunizations,
  fetchImmunization,
  fetchImmunizationError,
  fetchObservations,
  fetchObservation,
  fetchObservationError,
  fetchPatients,
  fetchPatientsWithContext,
  fetchPatientsError,
  fetchPatient,
  fetchPatientError,
  fetchProcedures,
  fetchProcedure,
  fetchProcedureError,
  fetchSequences,
  fetchSequence,
  fetchSequenceError,
  updateSequence,
  updateSequenceError,
  fetchDocumentReferences,
  fetchDocumentReference,
  fetchDocumentReferenceError,
  fetchMedia,
  fetchMedium,
  fetchMediumError,
  fetchVariants,
  fetchMedicationStatements,
  fetchMedicationAdministrations,
  fetchMedicationRequests,
  fetchGraphQLRequest,
  fetchGraphQLRequestError,
  fetchDiagnosticReports,
  fetchMedicationDispense,
  fetchQuestionnaires,
  fetchQuestionnaire,
  fetchQuestionnaireResponse,
  createQuestionnaire,
  updateQuestionnaire,
  fetchReferralRequest,
  fetchSpecimen,
  fetchImagingStudy,
  fetchGoal,
  updatePatient,
};
